import React, { ChangeEvent, FC, FocusEvent, FocusEventHandler, Fragment, SyntheticEvent, useState } from 'react';
import { formatDate } from 'utils/format';
import { TableCell, TextField, Button, FormControl } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { MaterialBatch } from 'models/material';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import { CONSTANTS } from 'config/constants';

type MaterialBatchItemInputProps = {
    index: number;
    saveBatch: (batch: MaterialBatch, index: number) => void;
};
const MaterialBatchItemInput: FC<MaterialBatchItemInputProps> = ({ index, saveBatch }: MaterialBatchItemInputProps) => {
    const { t: translate } = useTranslation(['common', 'actions-materials']);
    const [batch, setBatch] = useState<MaterialBatch>({
        batchNumber: '',
        quantity: 0,
        fullBatchQuantity: 0,
        createdOn: new Date(),
        closed: false,
    });
    const [errors, setErrors] = useState({ batchNumber: false, quantity: false, fullBatchQuantity: false });

    const validate = (values: MaterialBatch) => {
        if (!values.batchNumber) {
            return true;
        }
        if (!values.quantity) {
            return true;
        }
        if (!values.fullBatchQuantity) {
            return true;
        }
        return false;
    };

    const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (!value) {
            setErrors({ ...errors, batchNumber: true });
        } else {
            setErrors({ ...errors, batchNumber: false });
        }
        setBatch({ ...batch, [event.target.name]: value });
    };
    const handleQuantityChange = (quantity: string) => {
        if (!quantity || quantity == '0') {
            setErrors({ ...errors, quantity: true });
        } else {
            setErrors({ ...errors, quantity: false });
        }
        setBatch({ ...batch, quantity: parseFloat(quantity || '0') });
    };
    const handleFullBatchQuantityChange = (quantity: string) => {
        if (!quantity || quantity == '0') {
            setErrors({ ...errors, fullBatchQuantity: true });
        } else {
            setErrors({ ...errors, fullBatchQuantity: false });
        }
        // TODO: Remove set quantity to original quantity
        setBatch({ ...batch, fullBatchQuantity: parseFloat(quantity || '0'), quantity: parseFloat(quantity || '0') });
    };
    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
        const value = event.target?.value;
        if (parseInt(value) === 0) {
            event.target?.setSelectionRange(0, value.length);
        }
    };
    const handleBlur: FocusEventHandler<HTMLInputElement> = (event: SyntheticEvent) => {
        const target = event.target as HTMLInputElement;
        const name = target.name;
        const value = target.value;
        if (!value || value == '0') {
            setErrors({ ...errors, [name]: true });
        } else {
            setErrors({ ...errors, [name]: false });
        }
    };

    return (
        <Fragment>
            <TableCell>
                <FormControl fullWidth margin="dense">
                    <TextField
                        id="batch-number"
                        name="batchNumber"
                        required
                        onFocus={handleFocus}
                        label={translate('actions-materials:BATCH_NO')}
                        inputProps={{
                            style: { textAlign: 'right' },
                        }}
                        margin="dense"
                        fullWidth={true}
                        type="text"
                        size="small"
                        onChange={handleFieldChange}
                        onBlur={handleBlur}
                        value={batch.batchNumber}
                        error={Boolean(errors.batchNumber)}
                    />
                </FormControl>
            </TableCell>
            <TableCell>
                <NumberOnlyTextField
                    id="full-batch-quantity"
                    name="fullBatchQuantity"
                    required
                    margin="dense"
                    fullWidth={true}
                    type="text"
                    size="small"
                    label={translate('actions-materials:QUANTITY_FULL_BATCH')}
                    inputProps={{
                        style: { textAlign: 'right' },
                    }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onValueChange={handleFullBatchQuantityChange}
                    value={batch.fullBatchQuantity}
                    error={Boolean(errors.fullBatchQuantity)}
                />
            </TableCell>
            <TableCell sx={{ width: '120px' }}>{formatDate(new Date(batch.createdOn), 'date')}</TableCell>
            <TableCell sx={{ width: '120px' }} align="right">
                <Button
                    color="primary"
                    startIcon={<CheckIcon />}
                    disabled={validate(batch)}
                    onClick={() => saveBatch(batch, index)}
                    aria-label="save"
                >
                    {translate('common:SAVE')}
                </Button>
            </TableCell>
        </Fragment>
    );
};

export default MaterialBatchItemInput;
